import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import {
  createMilestoneCheckout,
  reconcileMilestonePayment,
} from '../../Services/marketplacePayments';
import { DEFAULT_DEPOSIT_RATE, percent, protectedPrice } from '../../Config/marketplace';
import { useI18n } from '../../i18n/I18nProvider';

const friendlyPaymentError = (error, en = false) => {
  const message = String(error?.message || '').replace(/^Firebase:\s*/i, '').trim();
  if (message && !message.toLowerCase().includes('functions/internal')) return message;
  return en ? 'The payment cannot be prepared right now. Try again shortly.' : 'Le paiement ne peut pas être préparé pour le moment. Réessayez dans quelques instants.';
};

const cleanPaymentReturnUrl = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  ['payment', 'session_id', 'demand', 'milestone'].forEach((key) => url.searchParams.delete(key));
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
};

const MilestonePaymentPanel = ({ demand }) => {
  const { language, locale } = useI18n();
  const en = language === 'en';
  const L = (fr, english) => (en ? english : fr);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');
  const returnHandled = useRef(false);
  const payments = demand?.payments || {};
  const depositPaid = payments?.deposit?.status === 'paid';
  const finalPaid = payments?.final?.status === 'paid';
  const canDeposit = demand?.demand_status === 'assigned' && !depositPaid;
  const canFinal = demand?.demand_status === 'finished' && depositPaid && !finalPaid;
  const plan = demand?.payment_plan || {};
  const contract = demand?.contract || {};
  const pricing = protectedPrice(plan.customer_total || contract.customer_total || contract.total_cost || 0);
  const currentCustomerAmount = canFinal
    ? Number(plan.final_customer_amount || pricing.customerTotal * (1 - DEFAULT_DEPOSIT_RATE))
    : Number(plan.deposit_customer_amount || pricing.customerTotal * DEFAULT_DEPOSIT_RATE);

  const recoverableMilestone = useMemo(() => {
    if (!depositPaid && payments?.deposit?.session_id) return 'deposit';
    if (demand?.demand_status === 'finished' && depositPaid && !finalPaid && payments?.final?.session_id) return 'final';
    return '';
  }, [demand?.demand_status, depositPaid, finalPaid, payments]);

  const reconcile = async (milestone, sessionId = '') => {
    setBusy(`sync-${milestone}`);
    setError('');
    setSyncMessage(L('Vérification du paiement en cours…', 'Checking the payment…'));
    try {
      const result = await reconcileMilestonePayment({
        demandId: demand.demand_id,
        milestone,
        sessionId,
      });
      if (result?.paid) {
        setSyncMessage(milestone === 'deposit'
          ? L('Acompte confirmé. Le suivi du chantier est à jour.', 'Deposit confirmed. Project tracking is up to date.')
          : L('Solde confirmé. Le projet est maintenant payé.', 'Final payment confirmed. The project is now paid.'));
      } else {
        setSyncMessage(L('Le prestataire bancaire n’a pas encore confirmé ce paiement. Réessayez dans quelques instants.', 'The payment provider has not confirmed this payment yet. Try again shortly.'));
      }
      return result;
    } catch (nextError) {
      setSyncMessage('');
      setError(friendlyPaymentError(nextError, en));
      return null;
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    if (returnHandled.current || typeof window === 'undefined' || !demand?.demand_id) return;
    const params = new URLSearchParams(window.location.search);
    const returnedDemandId = params.get('demand');
    if (returnedDemandId !== demand.demand_id) return;

    const paymentState = params.get('payment');
    const milestone = params.get('milestone');
    const sessionId = params.get('session_id') || '';
    returnHandled.current = true;

    if (paymentState === 'success' && ['deposit', 'final'].includes(milestone)) {
      reconcile(milestone, sessionId).finally(cleanPaymentReturnUrl);
    } else if (paymentState === 'cancelled') {
      setSyncMessage(L('Paiement annulé. Aucun débit n’a été enregistré.', 'Payment cancelled. No charge was recorded.'));
      cleanPaymentReturnUrl();
    }
  }, [demand?.demand_id]);

  if (!['assigned', 'completion_pending', 'completion_disputed', 'finished', 'paid', 'reviewed'].includes(demand?.demand_status)) return null;

  const pay = async (milestone) => {
    setBusy(milestone);
    setError('');
    setSyncMessage('');
    try {
      const result = await createMilestoneCheckout({ demandId: demand.demand_id, milestone });
      if (!result?.url) throw new Error(L('Le lien de paiement sécurisé n’a pas été généré.', 'The secure payment link was not generated.'));
      window.location.assign(result.url);
    } catch (nextError) {
      setError(friendlyPaymentError(nextError, en));
      setBusy('');
    }
  };

  return (
    <Panel>
      <Title>
        <AccountBalanceWalletOutlinedIcon />
        <div><strong>{L("Paiement sécurisé en 2 étapes", "Secure payment in two stages")}</strong><span>{L("L’artisan reste le vendeur de la prestation.", "The artisan remains the service provider.")}</span></div>
      </Title>
      <Steps>
        <Step $done={depositPaid}><b>{depositPaid ? <CheckCircleOutlineIcon /> : '1'}</b><span><strong>{L('Acompte', 'Deposit')} {percent(DEFAULT_DEPOSIT_RATE)}</strong><small>{depositPaid ? L('Payé et confirmé', 'Paid and confirmed') : L('Après acceptation du devis', 'After quote acceptance')}</small></span></Step>
        <Step $done={finalPaid}><b>{finalPaid ? <CheckCircleOutlineIcon /> : '2'}</b><span><strong>{L('Solde', 'Final balance')}</strong><small>{finalPaid ? L('Payé et confirmé', 'Paid and confirmed') : L('Après confirmation de fin des travaux par le client', 'After the client confirms completion')}</small></span></Step>
      </Steps>

      {demand?.demand_status === 'completion_pending' && (
        <Notice><InfoOutlinedIcon /> {L("L’artisan a déclaré les travaux terminés. Vérifiez le chantier puis confirmez ou signalez un problème avant le paiement du solde.", "The artisan declared the work complete. Check the job, then confirm it or report an issue before the final payment.")}</Notice>
      )}
      {demand?.demand_status === 'completion_disputed' && (
        <Warning><InfoOutlinedIcon /> {L("Un problème a été signalé. Le paiement du solde reste indisponible jusqu’à résolution entre les parties.", "An issue was reported. Final payment remains unavailable until both parties resolve it.")}</Warning>
      )}
      {demand?.demand_status === 'finished' && !depositPaid && (
        <Warning><InfoOutlinedIcon /> {L("La fin des travaux est confirmée, mais l’acompte n’est pas encore synchronisé dans LePetitBoulot. Vérifiez le paiement pour débloquer le solde.", "Completion is confirmed, but the deposit is not yet synchronised in LePetitBoulot. Check the payment to unlock the final balance.")}</Warning>
      )}
      {syncMessage && <SyncNotice role="status"><RefreshOutlinedIcon /> {syncMessage}</SyncNotice>}

      {(canDeposit || canFinal) && (
        <Breakdown>
          <span>{L('Prix TTC accepté', 'Accepted total')} <strong>{pricing.customerTotal.toLocaleString(locale, { style: 'currency', currency: 'EUR' })}</strong></span>
          <span>{canFinal ? L('Solde à régler', 'Final balance due') : L('Acompte à régler', 'Deposit due')} <strong>{currentCustomerAmount.toLocaleString(locale, { style: 'currency', currency: 'EUR' })}</strong></span>
          <small>{L("Paiement sécurisé, contrat, suivi du chantier et assistance Le Petit Boulot inclus. Aucun supplément ne sera ajouté au paiement.", "Secure payment, contract, project tracking and Le Petit Boulot support included. No extra fee will be added at checkout.")}</small>
        </Breakdown>
      )}

      <Actions>
        {canDeposit && <PayButton type="button" onClick={() => pay('deposit')} disabled={Boolean(busy)}><PaymentsOutlinedIcon /> {busy === 'deposit' ? L('Ouverture…', 'Opening…') : L('Payer l’acompte', 'Pay deposit')}</PayButton>}
        {canFinal && <PayButton type="button" onClick={() => pay('final')} disabled={Boolean(busy)}><PaymentsOutlinedIcon /> {busy === 'final' ? L('Ouverture…', 'Opening…') : L('Payer le solde', 'Pay final balance')}</PayButton>}
        {recoverableMilestone && (
          <RefreshButton type="button" onClick={() => reconcile(recoverableMilestone)} disabled={Boolean(busy)}>
            <RefreshOutlinedIcon /> {busy === `sync-${recoverableMilestone}` ? L('Vérification…', 'Checking…') : L('Vérifier le paiement', 'Check payment')}
          </RefreshButton>
        )}
      </Actions>
      {error && <Error role="alert">{error}</Error>}
      <LegalNote>{L("Le paiement est traité par un prestataire bancaire sécurisé. Le prix affiché est le prix total accepté : aucun supplément n’est ajouté au client.", "Payment is handled by a secure payment provider. The displayed amount is the accepted total: no extra charge is added to the client.")}</LegalNote>
    </Panel>
  );
};

const Panel = styled.section`display:grid;gap:.8rem;padding:1rem;border:1px solid #dfd2b8;border-radius:1rem;background:#fffaf0;min-width:0;`;
const Title = styled.div`display:flex;align-items:flex-start;gap:.55rem;color:#111;svg{font-size:1.1rem;margin-top:.1rem;}div{display:grid;}strong{font-size:.82rem;}span{color:#7a6c56;font-size:.66rem;line-height:1.4;}`;
const Steps = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.55rem;@media(max-width:30rem){grid-template-columns:1fr;}`;
const Step = styled.div`display:flex;align-items:center;gap:.55rem;padding:.7rem;border-radius:.8rem;background:${({$done})=>$done?'#e8f7ee':'#fff'};b{width:1.5rem;height:1.5rem;display:grid;place-items:center;flex:0 0 auto;border-radius:50%;background:${({$done})=>$done?'#17623a':'#111'};color:#fff;font-size:.68rem;}b svg{font-size:.95rem;margin:0;}span{display:grid;}strong{font-size:.74rem;}small{color:#766c5d;font-size:.65rem;line-height:1.35;}`;
const Breakdown = styled.div`display:grid;gap:.3rem;padding:.7rem .8rem;border-radius:.75rem;background:#fff;color:#595046;font-size:.69rem;span{display:flex;justify-content:space-between;gap:.8rem;}strong{color:#111;white-space:nowrap;}small{color:#7a6c56;line-height:1.45;}@media(max-width:30rem){span{align-items:flex-start;flex-direction:column;gap:.1rem;}}`;
const Notice = styled.p`display:flex;align-items:flex-start;gap:.4rem;margin:0;padding:.7rem .8rem;border-radius:.75rem;background:#eef5fb;color:#315d78;font-size:.7rem;line-height:1.5;svg{flex:0 0 auto;font-size:.95rem;margin-top:.08rem;}`;
const Warning = styled(Notice)`background:#fff1ef;color:#8c332c;`;
const SyncNotice = styled(Notice)`background:#eef7f1;color:#17623a;`;
const Actions = styled.div`display:flex;align-items:center;flex-wrap:wrap;gap:.55rem;@media(max-width:30rem){display:grid;grid-template-columns:1fr;}`;
const PayButton = styled.button`display:inline-flex;align-items:center;justify-content:center;gap:.4rem;min-height:2.7rem;padding:.62rem .85rem;border:0;border-radius:999px;background:#111;color:#fff;font:inherit;font-size:.76rem;font-weight:850;cursor:pointer;svg{font-size:1rem;}&:disabled{opacity:.55;cursor:wait;}@media(max-width:30rem){width:100%;}`;
const RefreshButton = styled(PayButton)`border:1px solid #d8c9ae;background:#fff;color:#5f471d;`;
const Error = styled.p`margin:0;color:var(--color-errorRed);font-size:.72rem;line-height:1.45;`;
const LegalNote = styled.small`color:#7a6c56;font-size:.62rem;line-height:1.45;`;

export default MilestonePaymentPanel;
